/**
 * Shared Filters popover + Sort menu for board chrome.
 * Matched control heights (Button md) and chip contrast (dark text on purple).
 */

import { ArrowUpDown, Check, ChevronDown, Filter } from 'lucide-react';
import { Typography } from '../../atoms/Typography';
import { Tag } from '../../atoms/Tag';
import { Button } from '../../atoms/Button';
import { DropdownMenu } from '../DropdownMenu';
import { cn } from '@/utils/cn';
import type {
  KanbanFilterDef,
  KanbanItemBase,
  KanbanSortOption,
} from './Kanban.config.types';

export interface KanbanFilterSortControlsProps<TItem extends KanbanItemBase> {
  filterDefs?: KanbanFilterDef<TItem>[];
  activeFilters?: Record<string, string | null>;
  onActiveFiltersChange?: (filters: Record<string, string | null>) => void;
  onClearFilters?: () => void;
  sortOptions?: KanbanSortOption<TItem>[];
  sortBy?: string | null;
  onSortByChange?: (sortBy: string | null) => void;
  /** Show Filters control (default true when filterDefs present). */
  showFilters?: boolean;
  /** Show Sort control (default true when sortOptions present). */
  showSorting?: boolean;
  filtersLabel?: string;
  clearFiltersLabel?: string;
  allOptionLabel?: string;
  sortLabel?: string;
  sortManualLabel?: string;
}

export function KanbanFilterSortControls<TItem extends KanbanItemBase>({
  filterDefs = [],
  activeFilters = {},
  onActiveFiltersChange,
  onClearFilters,
  sortOptions = [],
  sortBy,
  onSortByChange,
  showFilters = true,
  showSorting = true,
  filtersLabel = 'Filters',
  clearFiltersLabel = 'Clear filters',
  allOptionLabel = 'All',
  sortLabel = 'Sort',
  sortManualLabel = 'Manual',
}: KanbanFilterSortControlsProps<TItem>) {
  const activeFilterCount = Object.values(activeFilters).filter(
    (value) => value != null && value !== ''
  ).length;
  const hasActiveFilters = activeFilterCount > 0;
  const renderFilters = showFilters && filterDefs.length > 0;
  const renderSorting = showSorting && sortOptions.length > 0;

  const selectedSortOption =
    sortBy == null || sortBy === ''
      ? undefined
      : sortOptions.find((option) => option.id === sortBy);
  const hasActiveSort = selectedSortOption != null;
  const selectedSortLabel = selectedSortOption?.label ?? sortManualLabel;

  if (!renderFilters && !renderSorting) return null;

  return (
    <div className="inline-flex items-center gap-2">
      {renderFilters ? (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button
              type="contained"
              intent="muted"
              size="md"
              icon={<Filter />}
              label={activeFilterCount > 0 ? `(${activeFilterCount})` : undefined}
              title={filtersLabel}
              aria-label={
                activeFilterCount > 0
                  ? `${filtersLabel} (${activeFilterCount})`
                  : filtersLabel
              }
              selected={hasActiveFilters}
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="start" className="min-w-[260px] p-3">
            <div className="space-y-4">
              {filterDefs.map((def) => (
                <div key={def.id} className="space-y-2">
                  <Typography
                    variant="body-xs"
                    weight="semibold"
                    color="secondary"
                  >
                    {def.label}
                  </Typography>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/35"
                      onClick={() =>
                        onActiveFiltersChange?.({
                          ...activeFilters,
                          [def.id]: null,
                        })
                      }
                    >
                      <Tag
                        intent={
                          activeFilters[def.id] == null
                            ? 'primary'
                            : 'secondary'
                        }
                        variant={
                          activeFilters[def.id] == null ? 'solid' : 'subtle'
                        }
                        palette={
                          activeFilters[def.id] == null ? 'brand' : 'standard'
                        }
                        size="md"
                      >
                        {allOptionLabel}
                      </Tag>
                    </button>
                    {def.options.map((option) => {
                      const selected =
                        activeFilters[def.id] === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple/35"
                          onClick={() =>
                            onActiveFiltersChange?.({
                              ...activeFilters,
                              [def.id]: option.value,
                            })
                          }
                        >
                          <Tag
                            intent={selected ? 'primary' : 'secondary'}
                            variant={selected ? 'solid' : 'subtle'}
                            palette={selected ? 'brand' : 'standard'}
                            size="md"
                          >
                            {option.label}
                          </Tag>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {hasActiveFilters ? (
                <div className="flex justify-end pt-1">
                  <Button
                    type="text"
                    intent="secondary"
                    size="xs"
                    label={clearFiltersLabel}
                    onClick={onClearFilters}
                  />
                </div>
              ) : null}
            </div>
          </DropdownMenu.Content>
        </DropdownMenu>
      ) : null}

      {renderSorting ? (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button
              type="contained"
              intent="muted"
              size="md"
              icon={<ArrowUpDown />}
              rightIcon={<ChevronDown />}
              title={`${sortLabel}: ${selectedSortLabel}`}
              aria-label={`${sortLabel}: ${selectedSortLabel}`}
              label={`${selectedSortLabel}`}
              // Icon-only inside a narrow `@container` toolbar; no-op elsewhere.
              labelClassName="max-w-[9rem] truncate @max-lg:hidden"
              selected={hasActiveSort}
            />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="start" className="min-w-[11rem]">
            <DropdownMenu.Item
              onClick={() => onSortByChange?.(null)}
              className={cn(
                (sortBy == null || sortBy === '') && 'font-semibold'
              )}
            >
              {(sortBy == null || sortBy === '') ? (
                <Check className="h-4 w-4" />
              ) : (
                <span className="h-4 w-4" aria-hidden />
              )}
              {sortManualLabel}
            </DropdownMenu.Item>
            {sortOptions.map((option) => {
              const selected = sortBy === option.id;
              return (
                <DropdownMenu.Item
                  key={option.id}
                  onClick={() => onSortByChange?.(option.id)}
                  className={cn(selected && 'font-semibold')}
                >
                  {selected ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <span className="h-4 w-4" aria-hidden />
                  )}
                  {option.label}
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
